import { useEffect, useState } from 'react';
import { createClient } from '@supabase/supabase-js';
import { Link } from 'react-router-dom';

const supabase = createClient(
  import.meta.env.VITE_PUBLIC_SUPABASE_URL,
  import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY
);

interface User {
  id: string;
  email?: string;
  user_metadata?: {
    full_name?: string;
    avatar_url?: string;
  };
}

export default function Dashboard() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        window.REACT_APP_NAVIGATE('/login');
        return;
      }

      setUser(user);
    } catch (error) {
      console.error('Kullanıcı kontrolü hatası:', error);
      window.REACT_APP_NAVIGATE('/login');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
      window.REACT_APP_NAVIGATE('/');
    } catch (error) {
      console.error('Çıkış hatası:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-emerald-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center cursor-pointer">
              <h1 className="text-2xl font-bold text-gray-900">TechFlow</h1>
            </Link>
            <button
              onClick={handleLogout}
              className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-teal-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Çıkış Yap
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <div className="flex items-center gap-6">
            <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-full flex items-center justify-center text-white text-3xl font-bold">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 mb-1">
                Hoş geldiniz!
              </h2>
              <p className="text-gray-600">{user?.email}</p>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-user-line text-2xl text-teal-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Profil Bilgileri</h3>
            <p className="text-sm text-gray-600 mb-4">
              Hesap bilgilerinizi görüntüleyin ve düzenleyin
            </p>
            <Link 
              to="/profile"
              className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Görüntüle →
            </Link>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-settings-3-line text-2xl text-emerald-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Ayarlar</h3>
            <p className="text-sm text-gray-600 mb-4">
              Hesap ayarlarınızı yönetin
            </p>
            <button className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              Düzenle →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-shield-check-line text-2xl text-blue-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Güvenlik</h3>
            <p className="text-sm text-gray-600 mb-4">
              Şifrenizi değiştirin ve güvenlik ayarlarını yapın
            </p>
            <button className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              Yönet →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-notification-3-line text-2xl text-purple-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Bildirimler</h3>
            <p className="text-sm text-gray-600 mb-4">
              Bildirim tercihlerinizi düzenleyin
            </p>
            <button className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              Ayarla →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-file-list-3-line text-2xl text-orange-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Aktiviteler</h3>
            <p className="text-sm text-gray-600 mb-4">
              Son aktivitelerinizi görüntüleyin
            </p>
            <button className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              İncele →
            </button>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
              <i className="ri-question-line text-2xl text-pink-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Yardım</h3>
            <p className="text-sm text-gray-600 mb-4">
              Destek alın ve SSS'leri görüntüleyin
            </p>
            <button className="text-sm font-medium text-teal-600 hover:text-teal-700 transition-colors whitespace-nowrap cursor-pointer">
              Destek Al →
            </button>
          </div>
        </div>

        <div className="mt-8 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-2xl shadow-xl p-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-2xl font-bold mb-2">Hesabınız Aktif</h3>
              <p className="text-teal-50">
                Tüm özelliklere erişiminiz var. Platformumuzu keşfetmeye devam edin!
              </p>
            </div>
            <div className="hidden md:block">
              <div className="w-24 h-24 bg-white/20 rounded-full flex items-center justify-center">
                <i className="ri-check-line text-5xl"></i>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
